'use client';

import { useIsAppUser } from '@ps/functions/hooks/useIsAppUser';
import { Plus } from 'lucide-react';

import { useCommunityContext } from '../../contexts/community/useCommunityContext';
import { CommunityChannelsEmptyIcon } from '../common/CommunityChannelsEmptyIcon';

/**
 * Shown in the channel list pane when the creator has no channels.
 * Matches the design — green chat bubble icon + headline + CTA that
 * opens the create-channel modal.
 */
export function ChannelListEmptyState() {
  const { requestCreateChannel, canManage } = useCommunityContext();
  // Channel creation is a web-only editor — hidden inside the native app.
  const isAppUser = useIsAppUser();

  // CTA visible only where management is enabled (the dashboard owner surface)
  // AND only outside the native app. Matches legacy
  // `flows/community/EmptyChannelListView.tsx:71` (`isOwner && !error &&
  // !loading && !isAppUser`) — error / loading are already handled by the parent
  // `ChannelListMasterPane` switch (this component only renders when neither is
  // true). On the visitor surface `canManage` is false even for the owner, who
  // reaches creation via the header "Manage" entry.
  const canCreate = canManage && !isAppUser;

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
      <CommunityChannelsEmptyIcon className="h-auto w-[195px]" />
      <p className="text-raise/70 max-w-xs text-sm">
        You don&rsquo;t have any community channels yet!
        <br />
        Create a channel to engage with your followers.
      </p>
      {canCreate ? (
        <button
          type="button"
          onClick={requestCreateChannel}
          className="border-brand text-brand-text hover:bg-brand/10 inline-flex items-center gap-1 rounded-lg border px-4 py-2 text-xs"
        >
          <Plus className="h-4 w-4" aria-hidden /> Create Channel
        </button>
      ) : null}
    </div>
  );
}
